import { Component, Input, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { NavTabGroup } from 'src/app/features/nav-tab-group/nav-tab-group.model'
import {
  LauncherButtonConfig,
  LauncherStateChange,
} from 'src/app/models/quick-ticket.model'
import { LogonService } from 'src/app/services/logon/logon.service'
import { environment } from 'src/environments/environment'
import { AdobeLaunchService } from 'src/server/adobe-launch/adobe-launch.service'
import {
  GatekeeperService,
  PRODUCT_PROFILE_TRJ_LINK,
} from 'src/server/gatekeeper/gatekeeper.service'

@Component({
  selector: 'buy-button',
  providers: [],
  templateUrl: './buy-button.component.html',
  styleUrls: ['./buy-button.component.scss'],
})
export class BuyButtonComponent implements OnInit {
  @Input() fundInput: NavTabGroup
  @Input() size: string

  public vfeOldUrl = environment.vfeURLPrefix
  public vfeNewUrl = environment.vfeNewURLPrefix
  public vfaPersonal1Url = environment.vfaPersonal1Url
  public trjLinkEnabled: boolean

  public user = environment.UserLocation
  public href: string
  public isLoggedOn: any
  public launcherConfig: LauncherButtonConfig

  constructor(
    private readonly logonService: LogonService,
    public adobeLaunchService: AdobeLaunchService,
    private readonly router: Router,
    private readonly gatekeeperService: GatekeeperService,
  ) {}

  ngOnInit(): void {
    this.href = this.router.url

    this.gatekeeperService
      .isFeatureEnabled(PRODUCT_PROFILE_TRJ_LINK)
      .subscribe((enabled) => {
        this.trjLinkEnabled = enabled
        console.log('[TRJ] Link Enabled:', this.trjLinkEnabled)
      })

    this.launcherConfig = {
      labelText: 'Buy',
      ariaLabelText: `Buy ${this.fundInput.fundName}`,
      size: this.size,
      variant: 'red',
      fullWidth: true,
    }

    this.getLogOnData()
  }

  onStateChange(event: CustomEvent<LauncherStateChange>): void {
    const eventDetail = event.detail
    console.log('Quick Ticket State:', eventDetail) // logged for debugging
  }

  // eslint-disable-next-line
  async handleQuickTicketClickEvent() {
    await this.getLogOnData()

    const isExternalOpenETF =
      this.fundInput.isExternalFund &&
      this.fundInput.isETF &&
      !this.fundInput.isClosed &&
      !this.fundInput.isClosedToNewInvestors

    if (this.fundInput.isInternalFund) {
      this.adobeLaunchService.callToAction(
        'Buy',
        `VMF ${this.fundInput.ticker} - ${this.fundInput.fundName} - Dashboard Section - Fund ID ${this.fundInput.fundId}`,
        'Button',
      )
      this.trackBuyTouchPoint()
    } else if (this.fundInput.isExternalFund && !isExternalOpenETF) {
      this.trackBuyTouchPoint()
    }
  }

  // eslint-disable-next-line complexity,max-statements
  async buyFunds() {
    await this.getLogOnData()

    if (this.fundInput.isInternalFund) {
      if (
        this.fundInput.isETF &&
        !this.fundInput.isClosed &&
        !this.fundInput.isClosedToNewInvestors
      ) {
        this.ETFBuyButtonRedirect()
      } else if (
        this.fundInput.isMutualFund &&
        !this.fundInput.isClosed &&
        !this.fundInput.isClosedToNewInvestors
      ) {
        this.MFBuyButtonRedirect()
      } else if (this.fundInput.is529 && !this.fundInput.isClosed) {
        if (this.fundInput.isMutualFund) {
          this.MFBuyButtonRedirect()
        } else if (this.fundInput.isETF) {
          this.ETFBuyButtonRedirect()
        }
      }
      this.adobeLaunch()
    } else if (
      this.fundInput.isExternalFund &&
      this.fundInput.isETF &&
      !(this.fundInput.isClosed || this.fundInput.isClosedToNewInvestors)
    ) {
      this.ETFBuyButtonRedirect()
    } else if (this.fundInput.isExternalFund) {
      this.buyExternal()
    }
  }

  MFBuyButtonRedirect = () => {
    const base = this.trjLinkEnabled ? this.vfeNewUrl : this.vfeOldUrl
    const router = this.trjLinkEnabled ? 'trade-router/route' : 'BuySellRouter'
    const finalURL = base.concat(
      `${router}?investmentType=MUTL&nonRetirementMode=true&ticker=`
        .concat(this.fundInput.ticker)
        .concat('&transactionType=BUY'),
    )
    this.updateWindow(finalURL)
  }

  ETFBuyButtonRedirect = () => {
    const base = this.trjLinkEnabled ? this.vfeNewUrl : this.vfeOldUrl
    const router = this.trjLinkEnabled ? 'trade-router/route' : 'BuySellRouter'
    const finalURL = base.concat(
      `${router}?investmentType=EQUITY&nonRetirementMode=true&ticker=`
        .concat(this.fundInput.ticker)
        .concat('&transactionType=BUY'),
    )
    this.updateWindow(finalURL)
  }

  async adobeLaunch() {
    await this.getLogOnData()
    this.adobeLaunchService.callToAction(
      'Buy',
      `VMF ${this.fundInput.ticker} - ${this.fundInput.fundName} - Dashboard Section - Fund ID ${this.fundInput.fundId}`,
      'Button',
    )
    this.trackBuyTouchPoint()
  }

  async buyExternal() {
    await this.getLogOnData()
    this.trackBuyTouchPoint()
    const base = this.trjLinkEnabled
      ? environment.newExternalUrl
      : environment.externalUrl
    const router = this.trjLinkEnabled
      ? 'usa/trade-router/route'
      : 'us/BuySellRouter'
    const finalURL = base.concat(
      `${router}?investmentType=FUND_ACCESS_MUTUAL_FUND&transactionType=BUY&ticker=`.concat(
        this.fundInput.ticker,
      ),
    )
    this.updateWindow(finalURL)
  }

  private trackBuyTouchPoint() {
    const [action, type] = this.isLoggedOn
      ? ['buy', 'transact']
      : ['OOA', 'mmx']
    this.adobeLaunchService.touchPoint(
      this.href.substring(1),
      'Button',
      action,
      type,
    )
  }

  getLogOnData() {
    return this.logonService.getLoggedOn().then((loggedOn) => {
      this.isLoggedOn = loggedOn
    })
  }

  updateWindow(url) {
    window.location.assign(url)
  }
}
